import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  Loader2,
  CheckCircle2,
  RotateCcw,
  Newspaper,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Send,
  BarChart3,
  Rss,
} from "lucide-react";
import {
  listSocialQueue,
  markSocialShared,
  reviewSocialItem,
  queueFacebookShare,
  listSocialAudit,
} from "@/lib/api/social-queue.functions";
import { FacebookShareButton } from "@/components/site/FacebookShareButton";
import { History } from "lucide-react";


export const Route = createFileRoute(
  "/_authenticated/portal/admin/social-queue",
)({
  component: SocialQueuePage,
});

const SITE = "https://nkadimengdannylcs.com";
type StatusFilter = "all" | "pending" | "approved" | "rejected";

function AuditTrail({ kind, id }: { kind: "news" | "story"; id: string }) {
  const [open, setOpen] = useState(false);
  const fetchAudit = useServerFn(listSocialAudit);
  const { data, isLoading } = useQuery({
    queryKey: ["social-audit", kind, id],
    queryFn: () => fetchAudit({ data: { kind, itemId: id, limit: 50 } }),
    enabled: open,
  });
  return (
    <div className="mt-2 w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11px] text-navy hover:text-gold"
      >
        <History className="h-3 w-3" />
        {open ? "Hide" : "Show"} approval history
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-border bg-muted/30 p-2">
          {isLoading ? (
            <p className="text-[11px] text-muted-foreground">Loading…</p>
          ) : !data || data.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">
              No review history yet.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {data.map((r: any) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-start gap-2 text-[11px] leading-snug"
                >
                  <span
                    className={`rounded-full px-1.5 py-0.5 font-semibold uppercase ${
                      r.decision === "approved"
                        ? "bg-emerald-100 text-emerald-700"
                        : r.decision === "rejected"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {r.decision}
                  </span>
                  <span className="text-foreground/80">
                    by{" "}
                    <strong className="text-navy">
                      {r.reviewer_email ?? r.reviewer_id?.slice(0, 8) ?? "system"}
                    </strong>
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(r.created_at).toLocaleString("en-ZA", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  {r.note && (
                    <span className="w-full text-foreground/70">
                      “{r.note}”
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}



function SocialQueuePage() {
  const [onlyUnshared, setOnlyUnshared] = useState(true);
  const [status, setStatus] = useState<StatusFilter>("pending");
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");

  const fetchQueue = useServerFn(listSocialQueue);
  const markFn = useServerFn(markSocialShared);
  const reviewFn = useServerFn(reviewSocialItem);
  const queueFn = useServerFn(queueFacebookShare);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["social-queue", onlyUnshared, status],
    queryFn: () => fetchQueue({ data: { onlyUnshared, status, limit: 80 } }),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["social-queue"] });

  const mark = useMutation({
    mutationFn: (vars: {
      kind: "news" | "story";
      id: string;
      shared: boolean;
    }) => markFn({ data: vars }),
    onSuccess: invalidate,
  });

  const review = useMutation({
    mutationFn: (vars: {
      kind: "news" | "story";
      id: string;
      decision: "approved" | "rejected" | "pending";
      note?: string;
    }) => reviewFn({ data: vars }),
    onSuccess: () => {
      setNoteFor(null);
      setNoteText("");
      invalidate();
    },
  });

  const enqueue = useMutation({
    mutationFn: (vars: {
      kind: "news" | "story";
      id: string;
      shareUrl: string;
      caption: string;
    }) => queueFn({ data: vars }),
    onSuccess: invalidate,
  });

  const news = data?.news ?? [];
  const stories = data?.stories ?? [];

  function StatusBadge({ s }: { s: string | null }) {
    const status = s || "pending";
    const styles =
      status === "approved"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : status === "rejected"
          ? "bg-rose-50 text-rose-700 border-rose-200"
          : "bg-amber-50 text-amber-700 border-amber-200";
    return (
      <span
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles}`}
      >
        {status}
      </span>
    );
  }

  function ReviewBar({
    kind,
    id,
    approval,
    shareUrl,
    caption,
  }: {
    kind: "news" | "story";
    id: string;
    approval: string | null;
    shareUrl: string;
    caption: string;
  }) {
    const isApproved = approval === "approved";
    const isRejected = approval === "rejected";
    return (
      <div className="flex flex-col items-end gap-2">
        <StatusBadge s={approval} />
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <button
            disabled={review.isPending || isApproved}
            onClick={() =>
              review.mutate({ kind, id, decision: "approved" })
            }
            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
          >
            <ThumbsUp className="h-3 w-3" /> Approve
          </button>
          <button
            disabled={review.isPending || isRejected}
            onClick={() => {
              setNoteFor(`${kind}:${id}`);
              setNoteText("");
            }}
            className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50"
          >
            <ThumbsDown className="h-3 w-3" /> Reject
          </button>
          <button
            disabled={!isApproved || enqueue.isPending}
            title={
              !isApproved
                ? "Approve before queuing a share"
                : "Queue Facebook share"
            }
            onClick={() =>
              enqueue.mutate({ kind, id, shareUrl, caption })
            }
            className="inline-flex items-center gap-1 rounded-full border border-navy/20 bg-navy px-2 py-1 text-[11px] font-semibold text-white transition hover:opacity-90 disabled:opacity-40"
          >
            <Send className="h-3 w-3" /> Queue share
          </button>
        </div>
        {noteFor === `${kind}:${id}` && (
          <div className="mt-1 flex w-full max-w-xs flex-col gap-1">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={2}
              placeholder="Optional reviewer note"
              className="w-full rounded-md border border-border bg-background p-1.5 text-[11px]"
            />
            <div className="flex justify-end gap-1">
              <button
                onClick={() => setNoteFor(null)}
                className="text-[11px] text-muted-foreground hover:underline"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  review.mutate({
                    kind,
                    id,
                    decision: "rejected",
                    note: noteText.trim() || undefined,
                  })
                }
                className="text-[11px] font-semibold text-rose-700 hover:underline"
              >
                Confirm reject
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="container-narrow space-y-8 py-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Admin · Social
          </p>
          <h1 className="mt-1 font-display text-3xl text-navy">
            Facebook share queue
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Approve content before it goes live on Facebook. Approved items can
            be queued for automatic retries with exponential backoff.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/portal/admin/rss-sources"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-navy hover:border-navy"
          >
            <Rss className="h-3.5 w-3.5" /> RSS sources
          </Link>
          <Link
            to="/portal/admin/share-attempts"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-navy hover:border-navy"
          >
            <Send className="h-3.5 w-3.5" /> Retry queue
          </Link>
          <Link
            to="/portal/admin/social-analytics"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-navy hover:border-navy"
          >
            <BarChart3 className="h-3.5 w-3.5" /> Analytics
          </Link>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1 text-xs">
          {(["pending", "approved", "rejected", "all"] as StatusFilter[]).map(
            (s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`rounded-full px-3 py-1 capitalize transition ${
                  status === s
                    ? "bg-navy text-white"
                    : "text-foreground/70 hover:text-navy"
                }`}
              >
                {s}
              </button>
            ),
          )}
        </div>
        <label className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground/80">
          <input
            type="checkbox"
            checked={onlyUnshared}
            onChange={(e) => setOnlyUnshared(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Only show unshared
        </label>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading queue…
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="flex items-center gap-2 font-display text-xl text-navy">
              <Newspaper className="h-4 w-4 text-gold" /> News posts
              <span className="text-xs font-normal text-muted-foreground">
                ({news.length})
              </span>
            </h2>
            {news.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing here — caught up.
              </p>
            ) : (
              <ul className="space-y-3">
                {news.map((n: any) => {
                  const url = `${SITE}/news/${n.slug}`;
                  const caption = `${n.title}\n\n${n.excerpt ?? ""}`.trim();
                  return (
                    <li
                      key={n.id}
                      className="rounded-2xl border border-border bg-card p-4 shadow-soft"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-navy">{n.title}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {n.source_name} ·{" "}
                            {new Date(n.published_at).toLocaleString("en-ZA", {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                          {n.excerpt && (
                            <p className="mt-2 line-clamp-2 text-sm text-foreground/80">
                              {n.excerpt}
                            </p>
                          )}
                          {n.approval_note && (
                            <p className="mt-2 rounded-md bg-rose-50 px-2 py-1 text-[11px] text-rose-700">
                              Note: {n.approval_note}
                            </p>
                          )}
                          <div className="mt-2">
                            <FacebookShareButton
                              url={url}
                              caption={caption}
                              kind="news"
                              itemId={n.id}
                              itemSlug={n.slug}
                            />
                          </div>
                        </div>
                        <ReviewBar
                          kind="news"
                          id={n.id}
                          approval={n.approval_status}
                          shareUrl={url}
                          caption={caption}
                        />
                      </div>
                      <AuditTrail kind="news" id={n.id} />

                      <div className="mt-2 flex justify-end">
                        {n.shared_to_facebook_at ? (
                          <button
                            onClick={() =>
                              mark.mutate({
                                kind: "news",
                                id: n.id,
                                shared: false,
                              })
                            }
                            className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-rose-600"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Shared ·
                            undo <RotateCcw className="h-3 w-3" />
                          </button>
                        ) : (
                          <button
                            onClick={() =>
                              mark.mutate({
                                kind: "news",
                                id: n.id,
                                shared: true,
                              })
                            }
                            className="text-[11px] text-navy underline hover:text-gold"
                          >
                            Mark as shared manually
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="flex items-center gap-2 font-display text-xl text-navy">
              <Sparkles className="h-4 w-4 text-gold" /> Daily legal questions
              <span className="text-xs font-normal text-muted-foreground">
                ({stories.length})
              </span>
            </h2>
            {stories.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing here — caught up.
              </p>
            ) : (
              <ul className="space-y-3">
                {stories.map((s: any) => {
                  const url = `${SITE}/legal-stories#${s.id}`;
                  const caption =
                    `❓ ${s.question}\n\n${s.story}\n\nThink you know the answer? Read more on our site.`.trim();
                  return (
                    <li
                      key={s.id}
                      className="rounded-2xl border border-border bg-card p-4 shadow-soft"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-navy">
                            {s.question}
                          </p>
                          <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                            {s.category} ·{" "}
                            {new Date(s.publish_at).toLocaleString("en-ZA", {
                              day: "numeric",
                              month: "short",
                            })}
                          </p>
                          <p className="mt-2 line-clamp-3 text-sm text-foreground/80">
                            {s.story}
                          </p>
                          {s.approval_note && (
                            <p className="mt-2 rounded-md bg-rose-50 px-2 py-1 text-[11px] text-rose-700">
                              Note: {s.approval_note}
                            </p>
                          )}
                          <div className="mt-2">
                            <FacebookShareButton
                              url={url}
                              caption={caption}
                              kind="story"
                              itemId={s.id}
                            />
                          </div>
                        </div>
                        <ReviewBar
                          kind="story"
                          id={s.id}
                          approval={s.approval_status}
                          shareUrl={url}
                          caption={caption}
                        />
                      </div>
                      <AuditTrail kind="story" id={s.id} />

                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
